import { isEmpty } from 'lodash';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import {
  ACTIVATE_API_KEY,
  DELETE_API_KEY,
  PAUSE_API_KEY,
  ROTATE_API_KEY,
} from '../../constants/processTypes';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
  RESOURCE_TYPE_USER,
} from '../../constants/resourceTypes';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { apiKeyByResourceTypeSchema, apiKeySchema } from '../../schemas';
import { setTotalItems, setTotalPages } from '../app';
import { addProcess, removeProcess } from '../processes';

export const fetchResourceApiKeys = (resourceType, id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_RESOURCE_API_KEY_REQUEST,
        actionTypes.GET_RESOURCE_API_KEY_SUCCESS,
        actionTypes.GET_RESOURCE_API_KEY_FAILURE,
      ],
      endpoint: `/${resourceType}/${id}/api_keys`,
      payload: {},
      schema: [apiKeySchema],
      requestMethod: GET,
      //Always fetch api keys for a resource in collab mode
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
      requestInfo: {
        resourceType,
        id,
      },
    },
  });
};

export const fetchAllApiKeys = (page, sortCriteria, availableFilters) => (dispatch, getState) => {
  const {
    app: {
      visibilityFilters: { filters },
    },
  } = getState();

  const { processedFilters } = processSearchFilters(
    filters[SearchFilterViews.API_KEYS],
    availableFilters,
  );
  let requestMethod;
  let endpoint;
  let payload;

  if (isEmpty(processedFilters)) {
    requestMethod = GET;
    endpoint = '/api_keys';
    payload = {
      page,
      per_page: Constants.LISTING_ITEMS_PER_PAGE,
    };
  } else {
    requestMethod = POST;
    endpoint = `/api_keys/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}`;
    payload = { filters: processedFilters };
  }

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_ALL_API_KEYS_REQUEST,
        actionTypes.GET_ALL_API_KEYS_SUCCESS,
        actionTypes.GET_ALL_API_KEYS_FAILURE,
      ],
      endpoint,
      payload,
      schema: apiKeyByResourceTypeSchema,
      requestMethod,
      //Always fetch api keys for a resource in collab mode
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
      requestInfo: { view: SearchFilterViews.API_KEYS },
    },
  }).then((response) => {
    dispatch(setTotalPages(response.totalPages));
    dispatch(setTotalItems(response.totalItems));

    return response;
  });
};

// not in use
export const loadApiKeysSearchSuggestions = (filters, availableFilters) => (dispatch, getState) => {
  const { app } = getState();
  const aclQueryMode = app.accessControl.mode;

  const { sortKey, sortOrder } =
    Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
  const { processedFilters } = processSearchFilters(filters, availableFilters);

  if (isEmpty(processedFilters)) {
    return;
  }

  const params = new URLSearchParams({
    page: 1,
    per_page: RESOURCE_SUGGESTIONS_COUNT,
    sort_by: sortKey,
    sort_order: sortOrder,
  });

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SEARCH_API_KEYS_REQUEST,
        actionTypes.SEARCH_API_KEYS_SUCCESS,
        actionTypes.SEARCH_API_KEYS_FAILURE,
      ],
      endpoint: `/api_keys/search?${params}`,
      schema: apiKeyByResourceTypeSchema,
      requestMethod: POST,
      aclQueryMode,
      payload: { filters: processedFilters },
      requestInfo: { view: SearchFilterViews.API_KEYS },
    },
  });
};

export const addResourceApiKey =
  (resourceType, id, payload = {}) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_RESOURCE_API_KEY_REQUEST,
          actionTypes.CREATE_RESOURCE_API_KEY_SUCCESS,
          actionTypes.CREATE_RESOURCE_API_KEY_FAILURE,
        ],
        endpoint: `/${resourceType}/${id}/api_keys`,
        payload,
        schema: apiKeySchema,
        requestMethod: POST,
        //Always fetch api keys for a resource in collab mode
        // aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        requestInfo: {
          resourceType,
          id,
        },
      },
    });
  };

export const updateApiKey = (id, info) => (dispatch, getState) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_API_KEY_REQUEST,
        actionTypes.UPDATE_API_KEY_SUCCESS,
        actionTypes.UPDATE_API_KEY_FAILURE,
      ],
      endpoint: getApiKeyUri(id, getState()),
      payload: info,
      schema: apiKeySchema,
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  });
};

export const pauseApiKey = (id) => (dispatch, getState) => {
  dispatch(addProcess(PAUSE_API_KEY, 'Pausing API Key', { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PAUSE_API_KEY_REQUEST,
        actionTypes.PAUSE_API_KEY_SUCCESS,
        actionTypes.PAUSE_API_KEY_FAILURE,
      ],
      endpoint: getApiKeyUri(id, getState()) + '/pause',
      schema: apiKeySchema,
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  }).finally(() => {
    dispatch(removeProcess(PAUSE_API_KEY, { id }));
  });
};

export const activateApiKey = (id) => (dispatch, getState) => {
  dispatch(addProcess(ACTIVATE_API_KEY, 'Activating API Key', { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACTIVATE_API_KEY_REQUEST,
        actionTypes.ACTIVATE_API_KEY_SUCCESS,
        actionTypes.ACTIVATE_API_KEY_FAILURE,
      ],
      endpoint: getApiKeyUri(id, getState()) + '/activate',
      schema: apiKeySchema,
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  }).finally(() => {
    dispatch(removeProcess(ACTIVATE_API_KEY, { id }));
  });
};

export const rotateApiKey = (id) => (dispatch, getState) => {
  dispatch(addProcess(ROTATE_API_KEY, 'Rotating API Key', { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ROTATE_API_KEY_REQUEST,
        actionTypes.ROTATE_API_KEY_SUCCESS,
        actionTypes.ROTATE_API_KEY_FAILURE,
      ],
      endpoint: getApiKeyUri(id, getState()) + '/rotate',
      schema: apiKeySchema,
      requestMethod: PUT,
      requestInfo: {
        id,
      },
    },
  }).finally(() => {
    dispatch(removeProcess(ROTATE_API_KEY, { id }));
  });
};

export const deleteApiKey = (id) => (dispatch, getState) => {
  dispatch(addProcess(DELETE_API_KEY, 'Deleting API Key', { id }));

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_API_KEY_REQUEST,
        actionTypes.DELETE_API_KEY_SUCCESS,
        actionTypes.DELETE_API_KEY_FAILURE,
      ],
      endpoint: getApiKeyUri(id, getState()),
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  }).finally(() => {
    dispatch(removeProcess(DELETE_API_KEY, { id }));
  });
};

function getApiKeyUri(id, state) {
  const {
    entities: { apiKeys },
  } = state;
  const apiKey = apiKeys[id];
  let uri;

  switch (apiKey.resourceType) {
    case RESOURCE_TYPE_SOURCE:
      uri = `/data_sources/${apiKey.resourceId}`;
      break;

    case RESOURCE_TYPE_SINK:
      uri = `/data_sinks/${apiKey.resourceId}`;
      break;

    case RESOURCE_TYPE_USER:
      uri = `/users/${apiKey.resourceId}`;
      break;

    default:
      uri = `/data_sets/${apiKey.resourceId}`;
  }

  return uri + `/api_keys/${id}`;
}
